import Echo, { type PresenceChannel } from "laravel-echo";
import Pusher from "pusher-js";
import type { ApiResponse } from "~/types/ApiResponse";
import type { User } from "~/models/User";

export default defineNuxtPlugin(() => {
    window.Pusher = Pusher;
    const {
        public: { echo: config },
    } = useRuntimeConfig();

    const echo = ref<Echo>(
        new Echo({
            broadcaster: "reverb",
            key: config.reverbAppKey,
            wsHost: config.reverbHost,
            wsPort: config.reverbPort,
            wssPort: config.reverbPort,
            forceTLS: config.reverbScheme === "https",
            enabledTransports:
                config.reverbScheme === "https" ? ["wss"] : ["ws"],
            authorizer: (channel: any) => {
                const user = useSanctumUser().value;

                return {
                    authorize: async (socketId: any, callback: any) => {
                        try {
                            const response = await useApi().request(
                                "broadcasting/auth",
                                {
                                    method: "POST",
                                    headers: user?.extra?.broadcast_token
                                        ? {
                                              Authorization: `Bearer ${user.extra.broadcast_token}`,
                                          }
                                        : {},
                                    body: {
                                        socket_id: socketId,
                                        channel_name: channel.name,
                                    },
                                },
                            );

                            callback(null, response);
                        } catch (error) {
                            callback(error);
                        }
                    },
                };
            },
        }),
    );

    const authenticatedUsers = ref<any[]>([]);
    const authenticatedChannel = ref<PresenceChannel>();

    watchImmediate(
        useSanctumUser<ApiResponse<User>>(),
        async (toUser, fromUser) => {
            if (toUser) {
                authenticatedChannel.value = echo.value
                    .join(`online.${toUser.data.account.id}`)
                    .here((users: any) => {
                        authenticatedUsers.value = users;
                    })
                    .joining((user: any) => {
                        authenticatedUsers.value = [
                            user,
                            ...authenticatedUsers.value,
                        ];
                    })
                    .leaving((user: any) => {
                        authenticatedUsers.value =
                            authenticatedUsers.value.filter(
                                (existing) => existing.id !== user.id,
                            );
                    });
            } else if (fromUser) {
                authenticatedChannel.value = undefined;
            }
        },
    );

    return {
        provide: {
            echo,
            authenticatedChannel,
            authenticatedUsers,
        },
    };
});
